import { useMemo } from 'react';

import {
  mapAPILongAbsencesLimitedToAbsenceEntries,
  mapAPIShortAbsencesLimitedToAbsenceEntries,
} from '~/data/absences/data/mapping';
import { AbsenceLimited } from '~/data/absences/index';
import { AbsenceEntry, AbsenceSupersededReason } from '~/data/absences/types';
import { useGetEmployeeAbsenceLimitedData } from '~/data/absences/useGetEmployeeAbsenceLimitedData';
import { UseSWROutput } from '~/hooks/useSWR';

type PropsType = {
  employeeId: number;
  includeDeleted?: boolean;
};

type UseEmployeeAbsenceLimitedDataOutput = {
  absencesData?: AbsenceLimited;
  absenceEntries: AbsenceEntry[];
} & Omit<UseSWROutput<AbsenceLimited>, 'data'>;

export const useEmployeeAbsenceLimitedData = (
  props: PropsType,
): UseEmployeeAbsenceLimitedDataOutput => {
  const { employeeId, includeDeleted } = props;

  const { data: absencesData, ...rest } = useGetEmployeeAbsenceLimitedData(
    {
      employeeId,
    },
    {
      revalidateOnFocus: false,
    },
  );

  const { absenceEntries } = useMemo(() => {
    const absenceEntries: AbsenceEntry[] = absencesData
      ? [
          ...mapAPILongAbsencesLimitedToAbsenceEntries(
            absencesData.absenceLongs,
          ),
          ...mapAPIShortAbsencesLimitedToAbsenceEntries(
            absencesData.absenceShorts,
          ),
        ]
      : [];

    const defaultFilteredEntries = !!includeDeleted
      ? absenceEntries
      : absenceEntries.filter(
          (a) =>
            ![AbsenceSupersededReason.DELETE.toString()].includes(
              a?.supersededReason ?? '',
            ),
        );

    return {
      absenceEntries: defaultFilteredEntries,
    };
  }, [absencesData, includeDeleted]);

  return {
    absencesData,
    absenceEntries,
    ...rest,
  };
};
